<template>
    <app-layout>
        <Head title="Team Management"/>
        <template #header>
            <div class="flex justify-between">
                <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                    Team Management
                </h2>
                <Link :href="route('team.create')">
                    <jet-button>
                        Add Team Member
                    </jet-button>
                </Link>
            </div>
        </template>

        <!-- Billing Information Banner -->
        <div v-if="$page.props.subscribed" class="max-w-7xl mx-auto mb-6">
            <div class="w-full p-2 sm:px-6 lg:px-8">
                <div class="p-3 md:p-5 md:px-6 bg-blue-50 md:flex justify-between items-center rounded-lg border border-blue-200">
                    <p class="text text-blue-800 mb-3 md:mr-4 md:mb-0">
                        Each additional user beyond the organization owner is <span class="font-semibold">$10/month</span>. Users can be added or removed at any time.
                    </p>
                    
                </div>
            </div>
        </div>

        <!-- Pending Invitations Section -->
        <div v-if="pendingInvitations.length > 0" class="max-w-7xl mx-auto mb-6">
            <div class="w-full p-2 sm:px-6 lg:px-8">
                <div class="bg-white shadow-xl sm:rounded-lg p-4 sm:p-6 lg:p-8">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900">
                            Pending Invitations ({{ pendingInvitations.length }})
                        </h3>
                    </div>
                    <div class="space-y-3">
                        <div v-for="invitation in pendingInvitations" :key="invitation.id"
                             class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div class="flex-1">
                                <div class="flex items-center space-x-4">
                                    <div class="flex-1">
                                        <div class="flex items-center space-x-2">
                                            <p class="text-sm font-medium text-gray-900">
                                                {{ invitation.email }}
                                            </p>
                                            <role-badge v-if="invitation.role" :role="invitation.role" />
                                        </div>
                                        <p class="text-xs text-gray-500">
                                            Invited {{ formatDate(invitation.created_at) }} •
                                            <template v-if="isExpired(invitation)">
                                                <span class="text-red-600 font-medium">Expired</span> {{ formatDate(invitation.expires_at) }}
                                            </template>
                                            <template v-else>
                                                Expires {{ formatDate(invitation.expires_at) }}
                                            </template>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="flex space-x-2 ml-4">
                                <button @click="resendInvitation(invitation)"
                                        :disabled="resendingInvites[invitation.id]"
                                        class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                                    <svg v-if="resendingInvites[invitation.id]" class="animate-spin -ml-1 mr-1 h-3 w-3 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {{ resendingInvites[invitation.id] ? 'Sending...' : 'Resend' }}
                                </button>
                                <button @click="cancelInvitation(invitation)"
                                        class="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Team Members Table -->
        <div class="max-w-7xl mx-auto flex mb-6">
            <div class="w-full p-2 sm:px-6 lg:px-8">
                <div class="bg-white shadow-xl sm:rounded-lg p-4 sm:p-6 lg:p-8 relative">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">Active Users</h3>
                    <general-table
                        :columns="columns"
                        :url="route('team.table')"
                    />
                </div>
            </div>
        </div>

        <!-- Change Email Modal -->
        <div v-if="showChangeEmailModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-6 border max-w-lg w-full shadow-lg rounded-lg bg-white">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900">Change Email</h3>
                    <button @click="closeChangeEmailModal" class="text-gray-400 hover:text-gray-600">
                        <font-awesome-icon :icon="['fas', 'times']" />
                    </button>
                </div>
                <form @submit.prevent="submitChangeEmail" class="space-y-4">
                    <div>
                        <label for="change-email-input" class="block text-sm font-medium text-gray-700">
                            New Email
                        </label>
                        <input
                            id="change-email-input"
                            ref="changeEmailInput"
                            v-model="changeEmailForm.email"
                            type="email"
                            placeholder="person@example.com"
                            class="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                            :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': changeEmailForm.errors.email }"
                            required
                        >
                        <p v-if="changeEmailForm.errors.email" class="mt-2 text-sm text-red-600">
                            {{ Array.isArray(changeEmailForm.errors.email) ? changeEmailForm.errors.email[0] : changeEmailForm.errors.email }}
                        </p>
                    </div>
                    <div class="flex items-start">
                        <input
                            id="change-email-reset"
                            v-model="changeEmailForm.resetInvitation"
                            type="checkbox"
                            class="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        >
                        <label for="change-email-reset" class="ml-2 text-sm text-gray-700">
                            Reset name and password and send new email invitation.
                        </label>
                    </div>
                    <div class="flex justify-end space-x-3 pt-2">
                        <button
                            type="button"
                            @click="closeChangeEmailModal"
                            :disabled="changeEmailForm.loading"
                            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <jet-button
                            type="submit"
                            :disabled="changeEmailForm.loading"
                            :loading="changeEmailForm.loading"
                        >
                            Save Changes
                        </jet-button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Member Removal Preview Modal -->
        <div v-if="showRemovalPreview" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-6 border max-w-lg w-full shadow-lg rounded-lg bg-white">
                <div class="mt-3">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900">Remove Team Member Preview</h3>
                        <button @click="closeRemovalPreview" class="text-gray-400 hover:text-gray-600">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </div>

                    <div v-if="removalPreviewData" class="space-y-4">
                        <div>
                            <p class="text-sm text-gray-600 mb-2">
                                Removing team member:
                            </p>
                            <div class="bg-red-50 rounded-lg p-3 border border-red-200">
                                <div class="text-sm text-red-700 flex items-center">
                                    <font-awesome-icon :icon="['fas', 'user-times']" class="mr-2" />
                                    {{ removalPreviewData.user_name || removalPreviewData.user_email }}
                                </div>
                            </div>
                        </div>

                        <div v-if="removalPreviewData.invoice" class="border-t pt-4">
                            <h4 class="font-medium text-gray-900 mb-2">Next invoice preview</h4>
                            <div class="space-y-2 text-sm">
                                <div v-for="line in removalPreviewData.invoice.lines.data" :key="line.id" class="flex justify-between">
                                    <span>{{ line.description }}</span>
                                    <span>${{ (line.amount / 100).toFixed(2) }}</span>
                                </div>
                                <div class="border-t pt-2 flex justify-between font-semibold">
                                    <span>Total</span>
                                    <span>${{ (removalPreviewData.invoice.total / 100).toFixed(2) }}</span>
                                </div>
                                <p class="text-xs text-green-600 mt-1">
                                    <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1" />
                                    Prorated credit will be applied to your account.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="removalPreviewLoading" class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                        <p class="text-sm text-gray-600 mt-2">Calculating removal preview...</p>
                    </div>

                    <div v-if="removalPreviewError" class="text-center py-4">
                        <p class="text-sm text-red-600">{{ removalPreviewError }}</p>
                    </div>

                    <div class="flex gap-3 mt-6 pt-4 border-t">
                        <button
                            @click="confirmRemoval"
                            :disabled="removalPreviewLoading || removalPreviewError || removingMember"
                            class="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="removingMember" class="flex items-center justify-center">
                                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Removing...
                            </span>
                            <span v-else>Confirm Removal</span>
                        </button>
                        <button
                            @click="closeRemovalPreview"
                            :disabled="removingMember"
                            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Keep Member
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cancellation Preview Modal -->
        <div v-if="showCancellationPreview" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-6 border max-w-lg w-full shadow-lg rounded-lg bg-white">
                <div class="mt-3">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900">Cancel Invitation Preview</h3>
                        <button @click="closeCancellationPreview" class="text-gray-400 hover:text-gray-600">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </div>

                    <div v-if="cancellationPreviewData" class="space-y-4">
                        <div>
                            <p class="text-sm text-gray-600 mb-2">
                                Cancelling invitation for:
                            </p>
                            <div class="bg-red-50 rounded-lg p-3 border border-red-200">
                                <div class="text-sm text-red-700 flex items-center">
                                    <font-awesome-icon :icon="['fas', 'user-times']" class="mr-2" />
                                    {{ cancellationPreviewData.invitation_email }}
                                </div>
                            </div>
                        </div>

                        <div v-if="cancellationPreviewData.invoice" class="border-t pt-4">
                            <h4 class="font-medium text-gray-900 mb-2">Next invoice preview</h4>
                            <div class="space-y-2 text-sm">
                                <div v-for="line in cancellationPreviewData.invoice.lines.data" :key="line.id" class="flex justify-between">
                                    <span>{{ line.description }}</span>
                                    <span>${{ (line.amount / 100).toFixed(2) }}</span>
                                </div>
                                <div class="border-t pt-2 flex justify-between font-semibold">
                                    <span>Total</span>
                                    <span>${{ (cancellationPreviewData.invoice.total / 100).toFixed(2) }}</span>
                                </div>
                                <p class="text-xs text-green-600 mt-1">
                                    <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1" />
                                    Prorated credit will be applied to your account.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="cancellationPreviewLoading" class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                        <p class="text-sm text-gray-600 mt-2">Calculating cancellation preview...</p>
                    </div>

                    <div v-if="cancellationPreviewError" class="text-center py-4">
                        <p class="text-sm text-red-600">{{ cancellationPreviewError }}</p>
                    </div>

                    <div class="flex gap-3 mt-6 pt-4 border-t">
                        <button
                            @click="confirmCancellation"
                            :disabled="cancellationPreviewLoading || cancellationPreviewError || cancellingInvitation"
                            class="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="cancellingInvitation" class="flex items-center justify-center">
                                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Cancelling...
                            </span>
                            <span v-else>Confirm Cancellation</span>
                        </button>
                        <button
                            @click="closeCancellationPreview"
                            :disabled="cancellingInvitation"
                            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Keep Invitation
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout.vue";
import GeneralTable from "@/components/GeneralTable/GeneralTable.vue";
import TeamTableActionButtons from "@/components/Team/TeamTableActionButtons.vue";
import RoleBadge from "@/components/Team/RoleBadge.vue";
import NameWithRole from "@/components/Team/NameWithRole.vue";
import { router } from '@inertiajs/vue3';
import { markRaw } from 'vue';
import { mapActions } from 'pinia';
import { useGeneralTableStore } from '@/Stores/general_table';

export default {
    components: {
        AppLayout,
        GeneralTable,
    },
    props: {
        organization: Object,
        pendingInvitations: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            resendingInvites: {},
            showCancellationPreview: false,
            cancellationPreviewData: null,
            cancellationPreviewLoading: false,
            cancellationPreviewError: null,
            cancellingInvitation: false,
            currentInvitation: null,
            showRemovalPreview: false,
            removalPreviewData: null,
            removalPreviewLoading: false,
            removalPreviewError: null,
            removingMember: false,
            currentMember: null,
            showChangeEmailModal: false,
            changeEmailForm: {
                email: '',
                resetInvitation: false,
                loading: false,
                errors: {},
            },
            columns: [
                {
                    label: "Name",
                    name: "name",
                    sortable: true,
                    component: markRaw(NameWithRole),
                },
                {
                    label: "Email", 
                    name: "email",
                    sortable: true,
                },
                {
                    label: "Last Login",
                    name: "formatted_last_login",
                    sortable: true,
                },
                {
                    label: "",
                    name: "actions",
                    sortable: false,
                    width: "100px",
                    component: markRaw(TeamTableActionButtons),
                    eventHandler: (item, event) => {
                        if (event === 'remove-member') {
                            this.removeMember(item);
                        } else if (event === 'promote-member') {
                            this.updateMemberRole(item, 'admin');
                        } else if (event === 'demote-member') {
                            this.updateMemberRole(item, 'member');
                        } else if (event === 'change-email') {
                            this.openChangeEmailModal(item);
                        }
                    }
                }
            ],
        };
    },
    methods: {
        ...mapActions(useGeneralTableStore, ['fetchData']),

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        },
        
        async resendInvitation(invitation) {
            this.resendingInvites[invitation.id] = true;
            
            try {
                const response = await axios.post(route('team.invitation.resend', invitation.tra_id));
                
                if (response.data.success) {
                    this.$toasted.show(response.data.message || 'Invitation resent successfully', {
                        type: 'success',
                        duration: 3000
                    });
                    router.reload({ only: ['pendingInvitations'] });
                }
            } catch (error) {
                if (error.response && error.response.data.error) {
                    this.$toasted.show(error.response.data.error, {
                        type: 'error',
                        duration: 4000
                    });
                } else {
                    this.$toasted.show('Failed to resend invitation. Please try again.', {
                        type: 'error',
                        duration: 4000
                    });
                }
            } finally {
                this.resendingInvites[invitation.id] = false;
            }
        },
        
        async cancelInvitation(invitation) {
            // Store the invitation for use in confirmation
            this.currentInvitation = invitation;

            // Show preview modal and load preview data
            this.showCancellationPreview = true;
            this.cancellationPreviewLoading = true;
            this.cancellationPreviewError = null;
            this.cancellationPreviewData = null;

            try {
                const previewResponse = await axios.post(route('team.invitation.cancel.preview', invitation.tra_id));

                if (previewResponse.data.error) {
                    this.cancellationPreviewError = previewResponse.data.error;
                    this.cancellationPreviewLoading = false;
                    return;
                }

                this.cancellationPreviewData = previewResponse.data;
                this.cancellationPreviewLoading = false;

            } catch (error) {
                this.cancellationPreviewLoading = false;
                if (error.response && error.response.data.error) {
                    this.cancellationPreviewError = error.response.data.error;
                } else {
                    this.cancellationPreviewError = 'Failed to generate cancellation preview. Please try again.';
                }
            }
        },

        async confirmCancellation() {
            this.cancellingInvitation = true;

            try {
                const response = await axios.post(route('team.invitation.cancel', this.currentInvitation.tra_id));

                if (response.data.success) {
                    this.$toasted.show(response.data.message || 'Invitation cancelled successfully', {
                        type: 'success',
                        duration: 3000
                    });
                    this.closeCancellationPreview();
                    router.reload({ only: ['pendingInvitations'] });
                }

            } catch (error) {
                if (error.response && error.response.data.error) {
                    this.$toasted.show(error.response.data.error, {
                        type: 'error',
                        duration: 4000
                    });
                } else {
                    this.$toasted.show('Failed to cancel invitation. Please try again.', {
                        type: 'error',
                        duration: 4000
                    });
                }
            } finally {
                this.cancellingInvitation = false;
            }
        },

        closeCancellationPreview() {
            this.showCancellationPreview = false;
            this.cancellationPreviewData = null;
            this.cancellationPreviewError = null;
            this.cancellationPreviewLoading = false;
            this.cancellingInvitation = false;
            this.currentInvitation = null;
        },

        isExpired(invitation) {
            return new Date(invitation.expires_at) < new Date();
        },

        async removeMember(member) {
            this.currentMember = member;

            this.showRemovalPreview = true;
            this.removalPreviewLoading = true;
            this.removalPreviewError = null;
            this.removalPreviewData = null;

            try {
                const previewResponse = await axios.post(route('team.member.remove.preview', member.tra_id));

                if (previewResponse.data.error) {
                    this.removalPreviewError = previewResponse.data.error;
                    this.removalPreviewLoading = false;
                    return;
                }

                this.removalPreviewData = previewResponse.data;
                this.removalPreviewLoading = false;

            } catch (error) {
                this.removalPreviewLoading = false;
                if (error.response && error.response.data.error) {
                    this.removalPreviewError = error.response.data.error;
                } else {
                    this.removalPreviewError = 'Failed to generate removal preview. Please try again.';
                }
            }
        },

        async confirmRemoval() {
            this.removingMember = true;

            try {
                const response = await axios.delete(route('team.destroy', this.currentMember.tra_id));

                if (response.data.success) {
                    this.$toasted.show(response.data.message || 'Team member removed successfully', {
                        type: 'success',
                        duration: 3000
                    });
                    this.closeRemovalPreview();
                    this.fetchData();
                }

            } catch (error) {
                if (error.response && error.response.data.error) {
                    this.$toasted.show(error.response.data.error, {
                        type: 'error',
                        duration: 4000
                    });
                } else {
                    this.$toasted.show('Failed to remove team member. Please try again.', {
                        type: 'error',
                        duration: 4000
                    });
                }
            } finally {
                this.removingMember = false;
            }
        },

        closeRemovalPreview() {
            this.showRemovalPreview = false;
            this.removalPreviewData = null;
            this.removalPreviewError = null;
            this.removalPreviewLoading = false;
            this.removingMember = false;
            this.currentMember = null;
        },

        openChangeEmailModal(member) {
            this.currentMember = member;
            this.changeEmailForm.email = '';
            this.changeEmailForm.resetInvitation = false;
            this.changeEmailForm.errors = {};
            this.changeEmailForm.loading = false;
            this.showChangeEmailModal = true;
            this.$nextTick(() => {
                if (this.$refs.changeEmailInput) {
                    this.$refs.changeEmailInput.focus();
                }
            });
        },

        closeChangeEmailModal() {
            this.showChangeEmailModal = false;
            this.currentMember = null;
            this.changeEmailForm.email = '';
            this.changeEmailForm.resetInvitation = false;
            this.changeEmailForm.errors = {};
            this.changeEmailForm.loading = false;
        },

        async submitChangeEmail() {
            if (!this.currentMember) {
                return;
            }

            this.changeEmailForm.loading = true;
            this.changeEmailForm.errors = {};

            try {
                const response = await axios.put(route('team.update-email', this.currentMember.tra_id), {
                    email: this.changeEmailForm.email.trim(),
                    reset_invitation: this.changeEmailForm.resetInvitation,
                });

                if (response.data?.success) {
                    this.$toasted.show(response.data.message || 'Email updated successfully.', {
                        type: 'success',
                        duration: 3000,
                    });
                    router.reload();
                    this.closeChangeEmailModal();
                } else if (response.data?.message) {
                    this.$toasted.show(response.data.message, {
                        type: 'info',
                        duration: 3000,
                    });
                }
            } catch (error) {
                if (error.response?.status === 422 && error.response.data?.errors) {
                    this.changeEmailForm.errors = error.response.data.errors;
                } else if (error.response?.data?.error) {
                    this.$toasted.show(error.response.data.error, {
                        type: 'error',
                        duration: 4000,
                    });
                } else {
                    this.$toasted.show('Failed to update email. Please try again.', {
                        type: 'error',
                        duration: 4000,
                    });
                }
            } finally {
                this.changeEmailForm.loading = false;
            }
        },

        async updateMemberRole(member, role) {
            try {
                const response = await axios.put(route('team.update-role', member.tra_id), { role });

                if (response.data.success) {
                    this.$toasted.show(response.data.message || 'Role updated successfully.', {
                        type: 'success',
                        duration: 3000
                    });
                    this.fetchData();
                } else if (response.data.message) {
                    this.$toasted.show(response.data.message, {
                        type: 'info',
                        duration: 3000
                    });
                }
            } catch (error) {
                let errorMessage = 'Failed to update role. Please try again.';

                if (error.response) {
                    if (error.response.status === 422 && error.response.data.errors) {
                        const errors = error.response.data.errors;
                        const firstError = Object.values(errors)[0]?.[0];
                        if (firstError) {
                            errorMessage = firstError;
                        }
                    } else if (error.response.data && error.response.data.error) {
                        errorMessage = error.response.data.error;
                    }
                }

                this.$toasted.show(errorMessage, {
                    type: 'error',
                    duration: 4000
                });
            }
        },
    },
};
</script>
